import {z} from 'zod';

import {isNil} from 'ramda';

import {VatTypeSchema} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {MaterialDiscountOption} from '../utils/materialDiscountOption';
import {WorkDiscountOption} from '../utils/workDiscountOption';

const MAX_MARGIN = 100;

const discountSectionObjectSchema = z.object({
  isWorkExpanded: z.boolean(),
  selectedWorkOption: z.nativeEnum(WorkDiscountOption).nullable(),
  isMaterialExpanded: z.boolean(),
  selectedMaterialOption: z.nativeEnum(MaterialDiscountOption).nullable(),
  isSmallUnitMaterialExpanded: z.boolean(),
  isMinimumMarginExpanded: z.boolean(),
  isFixedValueWithVat: z.boolean(),
  isCustomVatRateExpanded: z.boolean(),
  customVatType: VatTypeSchema.nullable(),
  work: z
    .object({
      percentDiscount: z
        .object({
          discountRate: z.number().min(0).max(100).optional(),
        })
        .optional(),
      discountedWorkRates: z
        .object({
          discountedWorkRate: z
            .array(
              z.object({
                workRateType: z.string().optional(),
                workRatePriceWithoutVat: z.number().min(0).optional(),
              })
            )
            .optional(),
        })
        .optional(),
    })
    .optional(),
  material: z
    .object({
      percentDiscount: z
        .object({
          discountOnSalePriceRate: z.number().min(0).max(100).optional(),
          discountMarginToPurchasePriceRate: z.number().min(0).max(100).optional(),
        })
        .optional(),
    })
    .optional(),
  smallUnitMaterial: z
    .object({
      fixedValueAmountWithoutVat: z.number().min(0).nullable().optional(),
      fixedValueAmountWithVat: z.number().min(0).nullable().optional(),
    })
    .optional(),
  minimumMargin: z
    .object({
      material: z
        .number()
        .max(MAX_MARGIN, i18n.t('entity.warehouse.validations.maxMargin', {max: MAX_MARGIN}))
        .nullable()
        .optional(),
      labour: z
        .number()
        .max(MAX_MARGIN, i18n.t('entity.warehouse.validations.maxMargin', {max: MAX_MARGIN}))
        .nullable()
        .optional(),
    })
    .optional(),
});

type DiscountSectionFormInput = z.infer<typeof discountSectionObjectSchema>;

const getMissingOptionalSectionPaths = (data: DiscountSectionFormInput) =>
  [
    {
      isMissing:
        data.isSmallUnitMaterialExpanded &&
        !data.isFixedValueWithVat &&
        isNil(data.smallUnitMaterial?.fixedValueAmountWithoutVat),
      path: ['smallUnitMaterial', 'fixedValueAmountWithoutVat'],
    },
    {
      isMissing:
        data.isSmallUnitMaterialExpanded &&
        data.isFixedValueWithVat &&
        isNil(data.smallUnitMaterial?.fixedValueAmountWithVat),
      path: ['smallUnitMaterial', 'fixedValueAmountWithVat'],
    },
    {
      isMissing: data.isMinimumMarginExpanded && isNil(data.minimumMargin?.material),
      path: ['minimumMargin', 'material'],
    },
    {
      isMissing: data.isMinimumMarginExpanded && isNil(data.minimumMargin?.labour),
      path: ['minimumMargin', 'labour'],
    },
    {
      isMissing: data.isCustomVatRateExpanded && isNil(data.customVatType),
      path: ['customVatType'],
    },
  ]
    .filter((check) => check.isMissing)
    .map((check) => check.path);

export const discountSectionSchema = discountSectionObjectSchema.superRefine((data, ctx) => {
  const isPercentDiscountRequired =
    data.selectedWorkOption === WorkDiscountOption.PercentageDiscount && data.isWorkExpanded;
  const isDiscountedWorkRateRequired =
    data.selectedWorkOption === WorkDiscountOption.DiscountedWorkRate && data.isWorkExpanded;
  const isDiscountOnSalePriceRequired =
    data.selectedMaterialOption === MaterialDiscountOption.DiscountOnSalePrice &&
    data.isMaterialExpanded;
  const isMarginToPurchasePriceRequired =
    data.selectedMaterialOption === MaterialDiscountOption.MarginToPurchasePrice &&
    data.isMaterialExpanded;

  if (isPercentDiscountRequired && data.work?.percentDiscount?.discountRate === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['work', 'percentDiscount', 'discountRate'],
      message: i18n.t('general.errors.mixed.required'),
    });
  }

  if (isDiscountedWorkRateRequired) {
    data.work?.discountedWorkRates?.discountedWorkRate?.forEach((rate, index) => {
      if (!rate.workRateType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['work', 'discountedWorkRates', 'discountedWorkRate', index, 'workRateType'],
          message: i18n.t('general.errors.mixed.required'),
        });
      }

      if (rate.workRatePriceWithoutVat === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [
            'work',
            'discountedWorkRates',
            'discountedWorkRate',
            index,
            'workRatePriceWithoutVat',
          ],
          message: i18n.t('general.errors.mixed.required'),
        });
      }
    });
  }

  if (
    isDiscountOnSalePriceRequired &&
    data.material?.percentDiscount?.discountOnSalePriceRate === undefined
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['material', 'percentDiscount', 'discountOnSalePriceRate'],
      message: i18n.t('general.errors.mixed.required'),
    });
  }

  if (
    isMarginToPurchasePriceRequired &&
    data.material?.percentDiscount?.discountMarginToPurchasePriceRate === undefined
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['material', 'percentDiscount', 'discountMarginToPurchasePriceRate'],
      message: i18n.t('general.errors.mixed.required'),
    });
  }

  getMissingOptionalSectionPaths(data).forEach((path) => {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path,
      message: i18n.t('general.errors.mixed.required'),
    });
  });
});

export type DiscountSectionFormType = z.infer<typeof discountSectionSchema>;
